import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DefinedInitialDataOptions, QueryClient, queryOptions, UndefinedInitialDataOptions } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { CoreApiService } from '../../core-api.service';
import { ProfileService } from '../../profile/profile.service';
import { Annotation } from '../../shared/models/annotations.types';

@Injectable({
  providedIn: 'root',
})
export class AnnotationQueryService {

  private coreApi = inject(CoreApiService);
  private profileService = inject(ProfileService);
  private queryClient = inject(QueryClient);

  exhaustiveMode = toSignal(this.profileService.exhaustiveMode$);

  byProject(projectId: Signal<string>): UndefinedInitialDataOptions<Annotation[], Error, Annotation[], string[]> {
    return queryOptions({
      enabled: Boolean(projectId()),
      queryKey: ['annotations', 'byProject', projectId()],
      queryFn: () => (
        lastValueFrom(this.coreApi.annotations.get({
          filter: `parents.project=${projectId()}`,
          sort: 'modified:desc',
          exhaustive: this.exhaustiveMode(),
        }))
      ),
      placeholderData: [],
      meta: { invalidateOnExhaustiveMode: true },
    });
  }

  bySession(sessionId: Signal<string>): UndefinedInitialDataOptions<Annotation[], Error, Annotation[], string[]> {
    return queryOptions({
      enabled: Boolean(sessionId()),
      queryKey: ['annotations', 'bySession', sessionId()],
      queryFn: () => (
        lastValueFrom(this.coreApi.annotations.get({
          filter: `parents.session=${sessionId()}`,
          sort: 'modified:desc',
          exhaustive: this.exhaustiveMode(),
        }))
      ),
      placeholderData: [],
      meta: { invalidateOnExhaustiveMode: true },
    });
  }

  byFile(fileId: Signal<string>): DefinedInitialDataOptions<Annotation[], Error, Annotation[], string[]> {
    return queryOptions({
      enabled: Boolean(fileId()),
      queryKey: ['annotations', 'byFile', fileId()],
      select: (annotations: Annotation[]) => {
        // this is a stop-gap for before versioning is added to the API
        // they save new "versions" as the same label
        // we're filtering those duplicates out here
        // REMOVE when versioning is added
        const uniqueLabels = new Set();

        return annotations
          // janky way to get version count without API support
          // TODO: REMOVE when versioning is added
          .map(annotation => ({ ...annotation, version: annotations.filter(a => a.label === annotation.label).length }))
          .filter(annotation => {
            const duplicate = uniqueLabels.has(annotation.label);
            if (!duplicate) { uniqueLabels.add(annotation.label) }
            return !duplicate;
          })
      },
      queryFn: () => (
        lastValueFrom(this.coreApi.annotations.get({
          filter: `parents.file.file_id=${fileId()}`,
          sort: 'modified:desc',
          exhaustive: this.exhaustiveMode(),
        }))
      ),
      initialData: () => {
        const cachedAnnotations = this.getCachedAnnotations();
        const relevantAnnotations = cachedAnnotations?.filter(annotation => annotation?.parents.file.file_id === fileId());
        // we don't have a good mechanism for telling a cache miss from the data just not existing
        // so if we don't have any cached annotations we return undefined which will trigger the fetch
        return relevantAnnotations?.length > 0 ? relevantAnnotations : undefined;
      },
      // stale time needs to be non-zero so that the initial data is not treated as stale
      staleTime: 5000,
      placeholderData: [],
      meta: { invalidateOnExhaustiveMode: true },
    });
  }

  getCachedAnnotations(): Annotation[] {
    const prefetchedQueries = this.queryClient.getQueriesData<Annotation[]>({
      queryKey: ['annotations'],
    });
    const cachedAnnotations = prefetchedQueries?.flatMap(([, annotations]) => annotations);

    return cachedAnnotations;
  }
}
